import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Card,
  Typography,
  Grid2,
  DialogActions,
} from "@mui/material";
import AppInputField from "../../../components/CommonReusableComponent/AppInputField/AppInputField";
import AppButton from "../../../components/CommonReusableComponent/AppButton/AppButton";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Link } from "react-router-dom";
import Dropzone from "react-dropzone";
import viewIcon from "../../../assets/images/View.svg";
import { Icons } from "../../../config/MuiIcons.config";
import { AppTheme } from "../../../theme/App.theme";
import { useSnackbar } from "../../../contexts/Snackbar.context";
import {
  SNACKBAR_VARIANT,
  USE_FORMIK_SCHEMA,
} from "../../../config/Messages.config";
import { getFileNameFromInput } from "../../../common/CommonFunctions";

const NCActivitiesManagementModal = ({
  Open,
  ToggleFunction,
  Data = null,
  ModalType,
  UpdateParentData = () => {},
}) => {
  const { showSnackbar } = useSnackbar();
  const existingAttchments = Data?.attachments || [];

  // Activty Attchments/Documents
  const [activityAttachments, setActivityAttachments] = useState([
    ...existingAttchments,
  ]);

  // Modal type
  const isUpdateMode = ModalType === "UPDATE" ? true : false;
  const isCreateMode = ModalType === "CREATE" ? true : false;
  const isViewMode = ModalType === "VIEW" ? true : false;

  // Function to handle form reset after update
  const handleFormReset = () => {
    ToggleFunction();
    setActivityAttachments([]);
  };

  // Function to handle file drop
  const handleDropAcceptedFiles = (acceptedFiles) => {
    const acceptedFileTypes = [
      "image/png",
      "image/jpeg",
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    const acceptedFileSize = 5 * 1024 * 1024; // 5MB

    // Filter valid files based on type and size
    const validFiles = acceptedFiles.filter(
      (file) =>
        acceptedFileTypes.includes(file.type) && file.size <= acceptedFileSize
    );

    // Remove duplicates based on name + size
    const nonDuplicateFiles = validFiles.filter(
      (newFile) =>
        !activityAttachments.some(
          (existingFile) =>
            existingFile.name === newFile.name &&
            existingFile.size === newFile.size
        )
    );

    // Check for duplicate files and length limit
    if (nonDuplicateFiles.length < validFiles.length) {
      showSnackbar("Duplicate files are not allowed.", SNACKBAR_VARIANT.ERROR);
    } else if (activityAttachments.length + validFiles.length > 5) {
      showSnackbar("You can upload up to 5 files.", SNACKBAR_VARIANT.ERROR);
    } else {
      setActivityAttachments((prev) => [...prev, ...validFiles]);
    }
  };

  // Function to handle file rejection
  const handleRejectedFiles = (fileRejections) => {
    let reason = null;
    let is_too_large = null;
    let file_type_invalid = null;
    reason = fileRejections[0]?.errors[0]?.message;
    is_too_large = fileRejections[0]?.errors[0]?.code === "file-too-large";
    file_type_invalid =
      fileRejections[0]?.errors[0]?.code === "file-invalid-type";

    if (is_too_large) {
      showSnackbar(
        "File size exceeds the limit of 5MB.",
        SNACKBAR_VARIANT.ERROR
      );
    }

    if (file_type_invalid) {
      showSnackbar(
        "Invalid file type, please upload a valid file",
        SNACKBAR_VARIANT.ERROR
      );
    }

    if (reason && !is_too_large && !file_type_invalid) {
      showSnackbar(reason, SNACKBAR_VARIANT.ERROR);
    }
  };

  // Function to handle file delete
  const handleFileDelete = (indexToRemove) => {
    let updatedFiles = [...activityAttachments].filter(
      (_, index) => index !== indexToRemove
    );
    setActivityAttachments(updatedFiles);
  };

  // Function to handle file view in new tab
  const viewBlobInNewTab = (fileBlob) => {
    const blobURL = URL.createObjectURL(fileBlob);
    window.open(blobURL, "_blank");
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      activityName: (Data && Data?.activityName) || "",
      auditorObservation: (Data && Data?.auditorObservation) || "",
      capa: (Data && Data?.capa) || "",
    },
    validationSchema: Yup.object({
      activityName: Yup.string()
        .trim()
        .required(USE_FORMIK_SCHEMA.THIRD_PARTY_ACTIVITY_MODAL.ACTIVITY_NAME),
      auditorObservation: Yup.string()
        .trim()
        .required(
          USE_FORMIK_SCHEMA.THIRD_PARTY_ACTIVITY_MODAL.AUDITOR_OBSERVATION
        ),
      capa: Yup.string().required(
        USE_FORMIK_SCHEMA.THIRD_PARTY_ACTIVITY_MODAL.CAPA
      ),
    }),
    onSubmit: (values) => {
      if (!activityAttachments[0]) {
        showSnackbar(
          USE_FORMIK_SCHEMA.THIRD_PARTY_ACTIVITY_MODAL.ATTACHMENTS,
          SNACKBAR_VARIANT.ERROR
        );
      } else {
        const payload = {
          ...values,
          attachments: activityAttachments,
        };
        const id = Data?.id || null;
        const action = !Data?.id ? "ADD" : "UPDATE";

        UpdateParentData(action, id, payload);
        handleFormReset();
      }
    },
  });

  return (
    <React.Fragment>
      <Dialog
        open={Open}
        aria-labelledby="form-dialog-title"
        maxWidth="md"
        fullWidth
        slotProps={{
          style: {
            opacity: 0.7,
          },
        }}
      >
        <DialogTitle id="responsive-dialog-title">
          <div className="d-flex align-items-center">
            <Typography
              className="inter-medium-500"
              style={{
                fontSize: "18px",
              }}
            >
              {isCreateMode ? "Add NC Point" : isViewMode ? "View NC Point" : "Update NC Point"}
            </Typography>
            <div className="ms-auto"></div>
            <IconButton
              aria-label="close"
              onClick={() => handleFormReset()}
              style={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <Icons.Close />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent dividers>
          <div>
            <Grid2 container spacing={2} className="mb-3">
              <Grid2 size={12}>
                <Typography variant="body2" className="inter-regular-400 py-1">
                  Activity Name
                </Typography>
                <AppInputField
                  Name={"activityName"}
                  Id={"activityName"}
                  PlaceHolder={"Enter activity name"}
                  Value={validation.values.activityName || ""}
                  OnBlur={validation.handleBlur}
                  OnChange={validation.handleChange}
                  Disabled={isViewMode}
                  Error={
                    validation.touched.activityName &&
                    validation.errors.activityName
                      ? true
                      : false
                  }
                  HelperText={
                    validation.touched.activityName &&
                    validation.errors.activityName
                      ? validation.errors.activityName
                      : null
                  }
                />
              </Grid2>
              <Grid2 size={12}>
                <Typography variant="body2" className="inter-regular-400 py-1">
                  Auditor Observation
                </Typography>
                <AppInputField
                  Name={"auditorObservation"}
                  Id={"auditorObservation"}
                  IsMultiLine={true}
                  MaxRows={4}
                  PlaceHolder={"Enter auditor observation"}
                  Value={validation.values.auditorObservation || ""}
                  OnBlur={validation.handleBlur}
                  OnChange={validation.handleChange}
                  Disabled={isViewMode}
                  Error={
                    validation.touched.auditorObservation &&
                    validation.errors.auditorObservation
                      ? true
                      : false
                  }
                  HelperText={
                    validation.touched.auditorObservation &&
                    validation.errors.auditorObservation
                      ? validation.errors.auditorObservation
                      : null
                  }
                />
              </Grid2>
              <Grid2 size={12}>
                <Typography variant="body2" className="inter-regular-400 py-1">
                  CAPA
                </Typography>
                <AppInputField
                  Name={"capa"}
                  Id={"capa"}
                  IsMultiLine={true}
                  MaxRows={4}
                  PlaceHolder={"Enter auditor observation"}
                  Value={validation.values.capa || ""}
                  OnBlur={validation.handleBlur}
                  OnChange={validation.handleChange}
                  Disabled={isViewMode}
                  Error={
                    validation.touched.capa && validation.errors.capa
                      ? true
                      : false
                  }
                  HelperText={
                    validation.touched.capa && validation.errors.capa
                      ? validation.errors.capa
                      : null
                  }
                />
              </Grid2>
            </Grid2>

            {/* Dropzone Area */}
            <Grid2 container spacing={2} className="my-1">
              <Grid2 size={12}>
                <Typography variant="body2" className="inter-regular-400">
                  {activityAttachments?.length > 0
                    ? "Uploaded Files"
                    : activityAttachments?.length === 0 && isViewMode &&
                      "No files are uploaded"}
                </Typography>
              </Grid2>

              {activityAttachments.map((file, index) => {
                const fileName = getFileNameFromInput(file);
                return (
                  <Grid2
                    size={{ xs: 12, sm: 6, md: 4, lg: 4 }}
                    key={index}
                  >
                    <Card
                      variant="outlined"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "2px 4px",
                        bgcolor: "#f9f9f9",
                        borderRadius: "3px",
                        width: "100%",
                        boxShadow: "none",
                      }}
                    >
                      <div className="d-flex align-items-center">
                        <div className="d-grid ms-2">
                          <Typography
                            variant="caption"
                            noWrap={true}
                            className="inter-regular-400"
                          >
                            {fileName}
                          </Typography>
                        </div>
                      </div>
                      <div className="d-flex align-items-center justify-content-end gap-2">
                        {typeof file === "string" ? (
                          <Link
                            to={file}
                            target="_blank"
                            className="inter-regular-400"
                            title="View"
                          >
                            <img
                              src={viewIcon}
                              className="cursor-pointer"
                              alt={"View File"}
                              style={{ width: "22px", height: "20px" }}
                            />
                          </Link>
                        ) : (
                          <div title="View">
                            <img
                              src={viewIcon}
                              onClick={() => viewBlobInNewTab(file)}
                              className="cursor-pointer"
                              alt={"View File"}
                              style={{ width: "22px", height: "20px" }}
                            />
                          </div>
                        )}

                        {!isViewMode && (
                          <div title="Delete">
                            <Icons.Clear
                              className="cursor-pointer"
                              sx={{
                                fontSize: 19,
                              }}
                              onClick={() => handleFileDelete(index)}
                            />
                          </div>
                        )}
                      </div>
                    </Card>
                  </Grid2>
                );
              })}

              {!isViewMode && (
                <Grid2 size={12}>
                  <Typography
                    className="inter-regular-400 py-1"
                    variant="body2"
                  >
                    Select files or documents to upload
                  </Typography>
                  <Dropzone
                    onDrop={handleDropAcceptedFiles}
                    onDropRejected={handleRejectedFiles}
                    maxSize={5 * 1024 * 1024} // Max 5MB
                    accept={{
                      "image/png": [".png"],
                      "image/jpeg": [".jpg", ".jpeg"],
                      "application/pdf": [".pdf"],
                      "application/msword": [".doc"],
                      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
                        [".docx"],
                    }}
                    multiple
                  >
                    {({ getRootProps, getInputProps, isDragActive }) => (
                      <>
                        <div
                          style={{
                            height: "110px",
                            border: `1px dashed ${AppTheme.PRIMARY_COLOR}`,
                            borderRadius: "6px",
                            marginBottom: "2px",
                          }}
                        >
                          <div
                            style={{
                              height: "100%",
                              display: "flex",
                              textAlign: "center",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                            }}
                            {...getRootProps()}
                          >
                            <div>
                              <input {...getInputProps()} />
                              <Icons.CloudUploadIcon
                                sx={{
                                  fontSize: 40,
                                  color: `${AppTheme.PRIMARY_COLOR}`,
                                }}
                              />
                              {isDragActive ? (
                                <Typography
                                  variant="subtitle2"
                                  className="inter-regular-400 my-1"
                                >
                                  Drop your file here
                                </Typography>
                              ) : (
                                <>
                                  <Typography
                                    variant="subtitle2"
                                    className="inter-regular-400 my-1"
                                  >
                                    Drag your file here or{" "}
                                    <Link
                                      href="#"
                                      sx={{
                                        textDecoration: "none",
                                      }}
                                    >
                                      browse
                                    </Link>
                                  </Typography>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </Dropzone>
                  <Typography variant="caption" className="inter-regular-400">
                    Supports only .png, .jpg, .jpeg, .pdf, .doc and .docx files.
                    Maximum 5 files allowed, each up to 5MB in size.
                  </Typography>
                </Grid2>
              )}
            </Grid2>
          </div>
        </DialogContent>
        {!isViewMode && (
          <DialogActions>
            <div className="w-100">
              <Grid2 container spacing={1}>
                {!isUpdateMode && (
                  <Grid2 size={6} className="my-2">
                    <AppButton
                      Color={"error"}
                      OnPress={() => validation.handleReset()}
                    >
                      Reset
                    </AppButton>
                  </Grid2>
                )}
                <Grid2 size={isUpdateMode ? 12 : 6} className="my-2">
                  <AppButton OnPress={() => validation.handleSubmit()}>
                    {isUpdateMode ? "Update" : "Submit"}
                  </AppButton>
                </Grid2>
              </Grid2>
            </div>
          </DialogActions>
        )}
      </Dialog>
    </React.Fragment>
  );
};

export default NCActivitiesManagementModal;
